<template>
	<fieldset scrollId="ERInputs" class="pt-3 pb-3" :style="{ borderTop: 'none' }">
		<div class="container pt-5 pb-2">
			<div class="fieldgroup">
				<div class="row no-gutters">
					<div class="fieldset-title hasHover">
						<h2 class="heading">Electric Resistance Sizing</h2>

						<span
							:class="[
								'fieldgroup',
								'd-flex',
								'align-items-center',
								sizerStore.submitDataChanged && 'disabled'
							]"
						>
							<button
								type="button"
								@click.prevent="formData.sizeAdditionalER = !formData.sizeAdditionalER"
									:class="{
										'js-btn-toggle': true,
										'cover-parent': true,
										'ml-2': true,
										'mt-2': true,
										toggled: formData.sizeAdditionalER,
									}"
								>
								<i class="toggle-knob"></i>
							</button>

							<span
								v-if="sizerStore.submitDataChanged"
								class="ml-3 mt-2"
							>
								Form must be resubmitted with the new inputs.
							</span>
						</span>
					</div>
				</div>

				<div :class="['fieldgroup', !formData.sizeAdditionalER && 'disabled']">
					<div class="row no-gutters">
						<div class="fieldset-title hasHover">
							<UseIcon asset="sprite.svg#icon-gears" svgClass="fill-blue" />

							<h4 class="heading color-blue">Equipment Selection</h4>

							<button
								type="button"
								@click.prevent="sizerStore.erEquipmentSelect = !sizerStore.erEquipmentSelect"
								:class="{
									'js-btn-toggle': true,
									'cover-parent': true,
									'ml-2': true,
									toggled: sizerStore.erEquipmentSelect,
								}"
							>
								<i class="toggle-knob"></i>
							</button>
						</div>
					</div>

					<div
						:class="{
							row: true,
							'no-gutters': true,
							fieldgroup: true,
							'mt-2': true,
							disabled: !sizerStore.erEquipmentSelect,
						}"
					>
						<div class="col-3">
							<HPWHModelSelect
								:value="formData.modelName"
								@change="handleModelNameChange"
								:error="sizerStore.erEquipmentSelect ? outputCapacityError : ''"
							>
								<template v-slot:label >
									<label>Heat Pump Water Heater<br />Model</label>
								</template>
							</HPWHModelSelect>
						</div>

						<div class="col-2 offset-1">
							<FormInput
								dataKey="numHeatPumps"
								:onInput="debounce(getHPWHOutputCapacity, 500)"
							/>
						</div>

						<div class="col-2 offset-1">
							<FormInput
								dataKey="externalT_F"
								:onInput="debounce(getHPWHOutputCapacity, 500)"
							/>
						</div>

						<div class="col-2 offset-1">
							<FormInput
								dataKey="defrost_derate"
								:onInput="debounce(getHPWHOutputCapacity, 500)"
							/>
						</div>
					</div>

					<div class="row no-gutters fieldgroup">
						<div class="col-2">
							<FormInput
								dataKey="PCap_kW"
								:disabled="sizerStore.erEquipmentSelect"
							>
								<template v-slot:label>
									<span>Primary Capacity<br />(kW)</span>
								</template>
							</FormInput>
						</div>

						<div class="col-2 offset-1">
							<FormInput dataKey="PVol_G_atStorageT" />
						</div>

						<div class="col-2 offset-1">
							<FormInput dataKey="TMVol_G" />
						</div>

						<div
							:class="[
								'col-3',
								'offset-1',
								'd-flex',
								'flex-column',
								'align-items-start',
								'fieldgroup',
								disableERSubmit && 'disabled',
							]"
						>
							<button type="button" class="btn btn-box js-btn-expand er-sizing-button mt-auto" >
								Size Electric Resistance
							</button>
							<div class="suffix-wrap"></div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</fieldset>
</template>

<script>
import UseIcon from '@/components/UseIcon'
import FormInput from '@/components/FormInput'
import HPWHModelSelect from '@/components/HPWHModelSelect'
import jsonFetch from '@/utils/json-fetch'
import formData from '@/components/Sizer/form-data-store'
import sizerStore from '@/components/Sizer/sizer-store'
import debounce from 'lodash/debounce'

export default {
	name: 'ERInputs',

	components: {
		FormInput,
		UseIcon,
		HPWHModelSelect,
	},

	data: () => ({
		formData,
		sizerStore,
		outputCapacityError: undefined,
		hpwhModels: [],
		debounce,
	}),

	async mounted () {
		const { out: { minVolume, swingtankV} } = sizerStore

		formData.PVol_G_atStorageT = Math.ceil(minVolume / 50) * 50
		formData.TMVol_G = swingtankV
	},

	computed: {
		disableERSubmit () {
			const { PCap_kW, PVol_G_atStorageT, TMVol_G } = formData

			return [PCap_kW, PVol_G_atStorageT, TMVol_G].some(v => !v && v !== 0)
		},
	},

	methods: {
		handleModelNameChange ({ target: { value } }){
			const { getHPWHOutputCapacity } = this

			formData.modelName = value
			getHPWHOutputCapacity()
		},

		async getHPWHOutputCapacity () {
			const body = [
				'modelName',
				'numHeatPumps',
				'externalT_F',
				'storageT',
				'incomingT',
				'defrost_derate',
			].reduce((acc, key) => ({
				...acc,
				[key]: formData[key],
			}), {})

			if (Object.values(body).every(v => !!v || v === 0)) {
				const { outputCapacity, error } = await jsonFetch({
					url: '/sizer/hpwhOutputCapacity',
					body,
				})

				this.outputCapacityError = error
				formData.PCap_kW = Math.round(outputCapacity)
			}
		}
	},
}
</script>
